import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './ProjectWiseFinalAttendanceReport.css';
import jsPDF from "jspdf";
import "jspdf-autotable";
import ExcelJS from 'exceljs';
import { selectStyles } from '../../../assets/Styles/selectStyle';
import {
    getDistrict,
    getStates,
    getClients,
    fetchMonths,
    getFinalAttendanceReportProjectWise,
    getMonthlyLoginTimeReport
} from '../../../services/monthlyAttendanceService';
import { capitalizeFirstLetter, formatNumber, timeConverter } from '../../../utils/validations';
import { format } from 'date-fns';

const ProjectWiseFinalAttendanceReport = () => {
    const [state, setState] = useState(3);
    const [district, setDistrict] = useState(0);
    const [month, setMonth] = useState(null);
    const [year, setYear] = useState(null);
    const [stateOptions, setStateOptions] = useState([]);
    const [districtOptions, setDistrictOptions] = useState([]);
    const [monthOptions, setMonthOptions] = useState([]);
    const [project, setProject] = useState(null);

    // Core Report State
    const [attendanceData, setAttendanceData] = useState([]);
    const [clientResponse, setClientResponse] = useState([]);
    const [clients, setClients] = useState(null);
    const effectRan = useRef(false);

    // --- DRILL-DOWN STATES FOR DAY-WISE LOGIN LOGS ---
    const [viewLevel, setViewLevel] = useState("SUMMARY");
    const [selectedDistrictId, setSelectedDistrictId] = useState(null);
    const [selectedDistrictName, setSelectedDistrictName] = useState("");
    const [dayWiseLoginData, setDayWiseLoginData] = useState([]);
    const [totalDaysInSelectedMonth, setTotalDaysInSelectedMonth] = useState(30);

    // Dynamic state loaders
    const [isLoadingSummary, setIsLoadingSummary] = useState(false);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);

    const [isFocused, setIsFocused] = useState({
        state: false,
        month: false,
        year: false,
        project: false,
        clients: false,
    });
    const [errors, setErrors] = useState({});
    const yearInputRef = useRef(null);
    const [hasFetched, setHasFetched] = useState(false);
    const isFetchDataEnabled = state && month && year && clients;

    // Static helper context date fallbacks
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    // Helper conversion logic for string statuses vs hours values
    ;

    // Fetch states
    useEffect(() => {
        const fetchStates = async () => {
            try {
                const data = await getStates();
                setStateOptions(
                    data.map(item => ({
                        value: item.id,
                        label: capitalizeFirstLetter(item.stateName),
                    }))
                );
            } catch (error) {
                console.error('Error fetching states:', error);
                setErrors(prev => ({ ...prev, general: 'Failed to load states' }));
            }
        };
        if (!effectRan.current) {
            fetchStates();
        }
        return () => {
            effectRan.current = true;
        };
    }, []);

    // Fetch districts when state changes
    useEffect(() => {
        if (state) {
            const fetchDistricts = async () => {
                try {
                    const data = await getDistrict(state);
                    const filterData = [
                        { value: 0, label: "All Districts" },
                        ...data.filter(item => item.districtId === 39).map(item => ({
                            value: item.districtId,
                            label: item.districtName
                        }))
                    ];
                    setDistrictOptions(filterData);
                } catch (err) {
                    console.error('Error fetching districts:', err);
                    setErrors(prev => ({ ...prev, general: 'Failed to load districts' }));
                }
            };
            fetchDistricts();
        } else {
            setDistrictOptions([]);
            setDistrict(null);
        }
    }, [state]);

    // Fetch months when year changes
    useEffect(() => {
        if (year) {
            const loadMonths = async () => {
                try {
                    const months = await fetchMonths(year);
                    setMonthOptions(months);
                } catch (error) {
                    console.error("Error fetching months:", error);
                    setErrors((prev) => ({ ...prev, general: "Failed to load months" }));
                }
            };
            loadMonths();
        }
    }, [year]);

    // Fetch clients when state changes
    useEffect(() => {
        if (state) {
            const fetchClients = async () => {
                try {
                    const data = await getClients(state);
                    setClientResponse(data.map(client => ({
                        value: client.id,
                        label: client.clientName,
                    })));
                } catch (error) {
                    console.error('Error fetching clients:', error);
                }
            };
            fetchClients();
        } else {
            setClients(null);
        }
    }, [state]);

    // Reset data when filter changes
    useEffect(() => {
        setAttendanceData([]);
        setHasFetched(false);
        setViewLevel("SUMMARY");
    }, [state, district, month, year, clients]);

    const handleInputChange = (selectedOption, field) => {
        let value = selectedOption ? selectedOption.value : null;
        if (field === "district" && value === 0) {
            value = 0;
        }
        switch (field) {
            case 'state':
                setState(value);
                setDistrict(null);
                setMonth(null);
                break;
            case 'clients':
                setClients(value);
                break;
            case 'district':
                setDistrict(value);
                break;
            case 'month':
                setMonth(value);
                break;
            default:
                break;
        }
        setErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleYearChange = (date) => {
        setYear(date ? date.getFullYear() : null);
        setErrors(prev => ({ ...prev, year: null }));
    };

    // --- CORE HANDLER: FETCH MASTER REPORT SUMMARY ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setViewLevel("SUMMARY");
        setIsLoadingSummary(true);

        try {
            const rawData = await getFinalAttendanceReportProjectWise({
                stateId: state,
                districtId: district === 0 ? null : district,
                month,
                year,
            });

            if (!rawData || !Array.isArray(rawData)) {
                setAttendanceData([]);
                return;
            }

            const initializedData = [];
            rawData.forEach(districtItem => {
                const baseInfo = {
                    districtName: districtItem.districtName,
                    districtId: districtItem.districtId || 39
                };

                if (!districtItem.projects || districtItem.projects.length === 0) {
                    initializedData.push({ ...baseInfo, projectName: "-" });
                    return;
                }

                districtItem.projects.forEach(activeProject => {
                    const consolidatedHdoEmployees = (activeProject.totalHdoEmployees || 0) + (activeProject.totalHdopEmployees || 0);
                    const consolidatedHdoTotal = (activeProject.hdoTotal || 0) + (activeProject.hdopTotal || 0);
                    const consolidatedHdoAbsent = (activeProject.hdoAbsent || 0) + (activeProject.hdopAbsent || 0);
                    const consolidatedHdoWorked = (activeProject.hdoWorked || 0) + (activeProject.hdopWorked || 0);

                    initializedData.push({
                        ...baseInfo,
                        projectName: activeProject.projectName || "-",

                        // 1. Employee Headcounts
                        totalPmEmployees: activeProject.totalPmEmployees || 0,
                        totalPmdcEmployees: activeProject.totalPmdcEmployees || 0,
                        totalApmEmployees: activeProject.totalApmEmployees || 0,
                        totalHdoEmployees: consolidatedHdoEmployees,
                        totalCcoEmployees: activeProject.totalCcoEmployees || 0,
                        totalItmEmployees: activeProject.totalItmEmployees || 0,
                        totalCroEmployees: activeProject.totalCroEmployees || 0,
                        totalDeoEmployees: activeProject.totalDeoEmployees || 0,
                        totalsystemAdminEmployees: activeProject.totalsystemAdminEmployees || 0,
                        totaldbaEmployees: activeProject.totaldbaEmployees || 0,

                        // 2. Total Days
                        pmTotal: activeProject.pmTotal || 0,
                        pmdcTotal: activeProject.pmdcTotal || 0,
                        apmTotal: activeProject.apmTotal || 0,
                        hdoTotal: consolidatedHdoTotal,
                        ccoTotal: activeProject.ccoTotal || 0,
                        itmTotal: activeProject.itmTotal || 0,
                        croTotal: activeProject.croTotal || 0,
                        deoTotal: activeProject.deoTotal || 0,
                        systemAdminTotal: activeProject.systemAdminTotal || 0,
                        dbaTotal: activeProject.dbaTotal || 0,

                        // 3. Absent Days
                        pmAbsent: activeProject.pmAbsent || 0,
                        pmdcAbsent: activeProject.pmdcAbsent || 0,
                        apmAbsent: activeProject.apmAbsent || 0,
                        hdoAbsent: consolidatedHdoAbsent,
                        ccoAbsent: activeProject.ccoAbsent || 0,
                        itmAbsent: activeProject.itmAbsent || 0,
                        croAbsent: activeProject.croAbsent || 0,
                        deoAbsent: activeProject.deoAbsent || 0,
                        systemAdminAbsent: activeProject.systemAdminAbsent || 0,
                        dbaAbsent: activeProject.dbaAbsent || 0,

                        // 4. Worked Days
                        pmWorked: activeProject.pmWorked || 0,
                        pmdcWorked: activeProject.pmdcWorked || 0,
                        apmWorked: activeProject.apmWorked || 0,
                        hdoWorked: consolidatedHdoWorked,
                        ccoWorked: activeProject.ccoWorked || 0,
                        itmWorked: activeProject.itmWorked || 0,
                        croWorked: activeProject.croWorked || 0,
                        deoWorked: activeProject.deoWorked || 0,
                        systemAdminWorked: activeProject.systemAdminWorked || 0,
                        dbaWorked: activeProject.dbaWorked || 0,
                    });
                });
            });

            setAttendanceData(initializedData);
            setHasFetched(true);
        } catch (error) {
            console.error("Error fetching report data:", error);
            setErrors((prev) => ({ ...prev, general: "Failed to load summary dataset paths." }));
        } finally {
            setIsLoadingSummary(false);
        }
    };

    // --- MASTER ROW CLICK: FETCHES INNER EMPLOYEE DAY RECORDS ---
    const handleDistrictRowClick = async (districtId, districtName) => {
        let targetedDistrictId = districtId;
        console.log(`Row selected: Firing sub-drill queries for District Name: ${districtName}`);
        setIsLoadingDetail(true);

        try {
            const data = await getMonthlyLoginTimeReport(state, targetedDistrictId, year, month);
            console.log("Sub Drill Login API Response Captured:", data);

            const transformedData = data.employees.map((employee) => {
                const dayWiseStatus = {};
                for (let day = 1; day <= data.totalDays; day++) {
                    const dayValue = employee.attendanceDays[day];
                    if (dayValue) {
                        if (dayValue !== "H" && dayValue !== "WO" && dayValue !== "A" && dayValue !== "-" && dayValue.includes(":")) {
                            const timeParts = dayValue.split(":");
                            dayWiseStatus[day] = timeParts.length >= 2 ? `${timeParts[0]}:${timeParts[1]}` : dayValue;
                        } else {
                            dayWiseStatus[day] = dayValue;
                        }
                    } else {
                        dayWiseStatus[day] = "-";
                    }
                }

                return {
                    employeeId: employee.employeeUniqueId || employee.id,
                    fullName: employee.employeeName,
                    designation: employee.designation || "Staff",
                    contactNumber: employee.contactNumber || "-",
                    districtName: employee.assignedDistrict || districtName,
                    dayWiseStatus: dayWiseStatus,
                    payableDays: employee.payableDays,
                    lopDays: employee.lopDays,
                };
            });

            setTotalDaysInSelectedMonth(data.totalDays || 30);
            setDayWiseLoginData(transformedData);
            setSelectedDistrictName(districtName);
            setViewLevel("DETAIL");

        } catch (error) {
            console.error("Failed to compile internal employee logging array paths:", error);
            alert("Unable to populate log summary parameters. Please try again.");
        } finally {
            setIsLoadingDetail(false);
        }
    };

    // --- DYNAMIC EXCEL SUMMARY ENGINE ---
// Add this helper function at the beginning of your component (outside any function)
const formatNumberForExcel = (num) => {
    if (num === undefined || num === null || num === '') return 0;
    const number = typeof num === 'number' ? num : parseFloat(num);
    if (isNaN(number)) return 0;
    return number;
};

// Helper to format numbers as text with commas for Excel
const formatNumberWithCommas = (num) => {
    if (num === undefined || num === null) return '0';
    const number = typeof num === 'number' ? num : parseFloat(num);
    if (isNaN(number)) return '0';
    return number.toLocaleString('en-IN'); // Use 'en-IN' for Indian format or 'en-US' for US format
};

// --- DYNAMIC EXCEL SUMMARY ENGINE ---
const generateExcelSummary = async () => {
    if (!attendanceData || attendanceData.length === 0) {
        alert("No data available to generate export file.");
        return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Attendance Summary");

    // DYNAMIC TOTAL COLUMN CALCULATION
    let metrics = ["No of Employee", "Total Days in Month", "No of Days Absent", "No of Days Worked"];
    let categories = district
        ? ["PM", "DC", "APM", "Help Desk", "Call Center", "CRO", "System Admin"]
        : ["ITM", "CRO", "DEO", "HDO", "DBA"];

    const totalColumns = 3 + (metrics.length * categories.length);

    const getColumnLetter = (colNum) => {
        let letter = "";
        while (colNum > 0) {
            let rem = (colNum - 1) % 26;
            letter = String.fromCharCode(65 + rem) + letter;
            colNum = Math.floor((colNum - rem) / 26);
        }
        return letter;
    };

    const lastColLetter = getColumnLetter(totalColumns);

    // --- Document Title Banner ---
    worksheet.mergeCells(`A1:${lastColLetter}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "Project Wise Final Attendance Report Summary";
    titleCell.font = { size: 16, bold: true, color: { argb: "FFFFFF" } };
    titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "1F4E78" } };
    titleCell.alignment = { vertical: "middle", horizontal: "center" };
    worksheet.getRow(1).height = 40;

    const now = new Date();
    const reportDateTime = `${String(now.getDate()).padStart(2, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}-${now.getFullYear()} ${format(now, 'HH:mm')}`;
    const daysInMonth = new Date(Number(year) || now.getFullYear(), Number(month) || (now.getMonth() + 1), 0).getDate();

    const stateLabel = stateOptions.find(opt => opt.value === state)?.label || "N/A";
    const districtLabel = districtOptions.find(opt => opt.value === district)?.label || "N/A";
    const monthLabel = monthOptions.find(opt => opt.value === month)?.label || "N/A";
    const clientLabel = clientResponse.find(opt => opt.value === clients)?.label || "N/A";

    worksheet.mergeCells(`A3:E3`);
    worksheet.getCell("A3").value = `State: ${stateLabel} | District: ${districtLabel} | Client: ${clientLabel}`;
    worksheet.getCell("A3").font = { size: 11, bold: true };

    worksheet.mergeCells(`A4:F4`);
    worksheet.getCell("A4").value = `Month-Year: ${monthLabel} ${year || ""} | Days in Month: ${daysInMonth}`;
    worksheet.getCell("A4").font = { size: 11, bold: true };

    // RIGHT ALIGNED METADATA DATE ROW
    const startLetterNum = totalColumns - 4;
    const startLetter = getColumnLetter(startLetterNum);
    worksheet.mergeCells(`${startLetter}4:${lastColLetter}4`);
    const rightExportCell = worksheet.getCell(`${startLetter}4`);
    rightExportCell.value = `Export Date: ${reportDateTime}`;
    rightExportCell.font = { size: 11, bold: true, color: { argb: "595959" } };
    rightExportCell.alignment = { horizontal: "right", vertical: "middle" };

    worksheet.addRow([]); // Spacer row

    let headerRow1 = ["S.No", "District Name", "Project Name"];
    let headerRow2 = ["", "", ""];

    metrics.forEach(metric => {
        headerRow1.push(metric);
        for (let i = 1; i < categories.length; i++) headerRow1.push("");
        categories.forEach(cat => headerRow2.push(cat));
    });

    worksheet.addRow(headerRow1);
    worksheet.addRow(headerRow2);

    const thinBorder = {
        top: { style: "thin", color: { argb: "D9D9D9" } },
        left: { style: "thin", color: { argb: "D9D9D9" } },
        bottom: { style: "thin", color: { argb: "D9D9D9" } },
        right: { style: "thin", color: { argb: "D9D9D9" } }
    };

    const headerFills = ["FFF2CC", "D9E1F2", "FCE4D6", "E2EFDA"];
    const baseHeaderColor = "4472C4";

    const row6 = worksheet.getRow(6);
    row6.height = 26;
    let colIndex = 4;

    metrics.forEach((metric, i) => {
        const startCol = colIndex;
        const endCol = startCol + categories.length - 1;
        worksheet.mergeCells(6, startCol, 6, endCol);
        for (let col = startCol; col <= endCol; col++) {
            const cell = row6.getCell(col);
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: headerFills[i] } };
            cell.font = { bold: true, size: 11, color: { argb: "000000" } };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.border = thinBorder;
        }
        colIndex += categories.length;
    });

    for (let c = 1; c <= 3; c++) {
        worksheet.mergeCells(6, c, 7, c);
        const cell = row6.getCell(c);
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: baseHeaderColor } };
        cell.font = { bold: true, color: { argb: "FFFFFF" } };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = thinBorder;
    }

    const row7 = worksheet.getRow(7);
    row7.height = 22;
    metrics.forEach((_, i) => {
        const startCol = 4 + i * categories.length;
        const endCol = startCol + categories.length - 1;
        for (let col = startCol; col <= endCol; col++) {
            const cell = row7.getCell(col);
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: headerFills[i] } };
            cell.font = { bold: true, size: 10 };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.border = thinBorder;
        }
    });

    let bodyFields = district
        ? [
            "totalPmEmployees", "totalPmdcEmployees", "totalApmEmployees", "totalHdoEmployees", "totalCcoEmployees", "totalCroEmployees", "totalsystemAdminEmployees",
            "pmTotal", "pmdcTotal", "apmTotal", "hdoTotal", "ccoTotal", "croTotal", "systemAdminTotal",
            "pmAbsent", "pmdcAbsent", "apmAbsent", "hdoAbsent", "ccoAbsent", "croAbsent", "systemAdminAbsent",
            "pmWorked", "pmdcWorked", "apmWorked", "hdoWorked", "ccoWorked", "croWorked", "systemAdminWorked"
        ]
        : [
            "totalItmEmployees", "totalCroEmployees", "totalDeoEmployees", "totalHdoEmployees", "totaldbaEmployees",
            "itmTotal", "croTotal", "deoTotal", "hdoTotal", "dbaTotal",
            "itmAbsent", "croAbsent", "deoAbsent", "hdoAbsent", "dbaAbsent",
            "itmWorked", "croWorked", "deoWorked", "hdoWorked", "dbaWorked"
        ];

    // Append Rows - FORMATTED WITH COMMAS
    attendanceData.forEach((row, index) => {
        let rowData = [
            index % 2 === 0 ? Math.floor(index / 2) + 1 : "",
            index % 2 === 0 ? row.districtName : "",
            row.projectName || "-"
        ];
        bodyFields.forEach(field => {
            const value = row[field] !== undefined ? row[field] : 0;
            // Format numbers with commas for better readability
            rowData.push(formatNumberWithCommas(value));
        });

        const addedRow = worksheet.addRow(rowData);
        addedRow.height = 20;
        addedRow.eachCell((cell, colNum) => {
            cell.border = thinBorder;
            cell.alignment = { vertical: "middle", horizontal: colNum <= 3 ? "left" : "right" }; // Changed to right alignment for numbers
            cell.font = { size: 10 };
        });
    });

    // Dynamic Row-Spans (Merge Cells) on District Groups
    let currentStartRow = 8;
    for (let i = 0; i < attendanceData.length; i += 2) {
        if (i + 1 < attendanceData.length) {
            worksheet.mergeCells(`A${currentStartRow}:A${currentStartRow + 1}`);
            worksheet.mergeCells(`B${currentStartRow}:B${currentStartRow + 1}`);
            worksheet.getCell(`A${currentStartRow}`).alignment = { vertical: "middle", horizontal: "center" };
            worksheet.getCell(`B${currentStartRow}`).alignment = { vertical: "middle", horizontal: "left" };
        }
        currentStartRow += 2;
    }

    // TOTAL ROW WITH COMMA FORMATTING
    const calcTotal = field => attendanceData.reduce((sum, r) => sum + (Number(r[field]) || 0), 0);
    let totalRowData = ["Total", "", ""];
    bodyFields.forEach(f => totalRowData.push(formatNumberWithCommas(calcTotal(f))));

    const totalRow = worksheet.addRow(totalRowData);
    totalRow.height = 24;
    totalRow.font = { bold: true, size: 11 };
    worksheet.mergeCells(`A${totalRow.number}:C${totalRow.number}`);

    totalRow.eachCell((cell, colNum) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE699" } };
        cell.border = thinBorder;
        cell.alignment = { vertical: "middle", horizontal: colNum <= 3 ? "left" : "right" }; // Right align numbers
    });
    worksheet.getCell(`A${totalRow.number}`).alignment = { vertical: "middle", horizontal: "left" };

    worksheet.columns.forEach((col, index) => {
        if (index === 0) col.width = 6;
        else if (index === 1) col.width = 24;
        else if (index === 2) col.width = 15;
        else col.width = 15;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Project_Wise_Final_Attendance_Summary.xlsx`;
    link.click();
};

    const generateExcelDetail = async () => {
        if (!dayWiseLoginData || dayWiseLoginData.length === 0) return;
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Login Time Log");

        // Updated: +7 instead of +5 (S.No, District, Employee Name, Designation, Contact Number, Payable Days, LOP Days)
        const totalDetailCols = totalDaysInSelectedMonth + 7;
        const getColumnLetter = (colNum) => {
            let letter = "";
            while (colNum > 0) {
                let rem = (colNum - 1) % 26;
                letter = String.fromCharCode(65 + rem) + letter;
                colNum = Math.floor((colNum - rem) / 26);
            }
            return letter;
        };
        const endDetailColLetter = getColumnLetter(totalDetailCols);

        // --- Document Title Banner ---
        worksheet.mergeCells(`A1:${endDetailColLetter}1`);
        const titleCell = worksheet.getCell("A1");
        titleCell.value = `District Employees Login Time Report: ${selectedDistrictName}`;
        titleCell.font = { bold: true, size: 16, color: { argb: "FFFFFF" } };
        titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "1F4E78" } };
        titleCell.alignment = { horizontal: "center", vertical: "middle" };
        worksheet.getRow(1).height = 40;

        worksheet.getColumn(1).width = 8;   // S.No
        worksheet.getColumn(2).width = 20;  // District
        worksheet.getColumn(3).width = 25;  // Employee Name
        worksheet.getColumn(4).width = 25;  // Designation
        worksheet.getColumn(5).width = 18;  // Contact Number
        worksheet.getColumn(totalDetailCols - 1).width = 15;  // Payable Days
        worksheet.getColumn(totalDetailCols).width = 15;      // LOP Days

        const now = new Date();
        const reportDateTime = `${String(now.getDate()).padStart(2, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}-${now.getFullYear()} ${format(now, 'HH:mm')}`;
        const daysInMonth = new Date(Number(year) || now.getFullYear(), Number(month) || (now.getMonth() + 1), 0).getDate();

        const stateLabel = stateOptions.find(opt => opt.value === state)?.label || "N/A";
        const districtLabel = selectedDistrictName || "N/A";
        const monthLabel = monthOptions.find(opt => opt.value === month)?.label || "N/A";
        const clientLabel = clientResponse.find(opt => opt.value === clients)?.label || "N/A";

        worksheet.mergeCells(`A3:E3`);
        worksheet.getCell("A3").value = `State: ${stateLabel} | District: ${districtLabel} | Client: ${clientLabel}`;
        worksheet.getCell("A3").font = { size: 11, bold: true };

        worksheet.mergeCells(`A4:K4`);
        worksheet.getCell("A4").value = `Month-Year: ${monthLabel} ${year || ""} | Days in Month: ${daysInMonth}`;
        worksheet.getCell("A4").font = { size: 11, bold: true };

        // RIGHT ALIGNED EXPORT DATE MERGE RANGE FOR DETAILS SHEET
        const rightLabelStartCol = totalDetailCols - 4;
        const startLetter = getColumnLetter(rightLabelStartCol);
        worksheet.mergeCells(`${startLetter}4:${endDetailColLetter}4`);
        const rightExportCell = worksheet.getCell(`${startLetter}4`);
        rightExportCell.value = `Export Date: ${reportDateTime}`;
        rightExportCell.font = { size: 11, bold: true, color: { argb: "595959" } };
        rightExportCell.alignment = { horizontal: "right", vertical: "middle" };

        worksheet.addRow([]); // Spacer row

        // Updated headers to include Payable Days and LOP Days
        const headers = ["S.No", "District", "Employee Name", "Designation", "Contact Number"];
        for (let d = 1; d <= totalDaysInSelectedMonth; d++) {
            headers.push(`Day-${d}`);
        }
        headers.push("Payable Days", "LOP Days");

        worksheet.addRow(headers);
        const headerRow = worksheet.getRow(6);
        headerRow.height = 26;
        headerRow.eachCell(cell => {
            cell.font = { bold: true, color: { argb: "FFFFFF" }, size: 10 };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "4472C4" } };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.border = {
                top: { style: "thin", color: { argb: "BFBFBF" } },
                left: { style: "thin", color: { argb: "BFBFBF" } },
                bottom: { style: "thin", color: { argb: "BFBFBF" } },
                right: { style: "thin", color: { argb: "BFBFBF" } }
            };
        });

        dayWiseLoginData.forEach((emp, index) => {
            // Updated rowData to include payableDays and lopDays at the end
            const rowData = [index + 1, emp.districtName, emp.fullName, emp.designation, emp.contactNumber];
            for (let d = 1; d <= totalDaysInSelectedMonth; d++) {
                rowData.push(timeConverter(emp.dayWiseStatus[d]));
            }
            rowData.push(emp.payableDays, emp.lopDays); // Add the new columns

            const dataRow = worksheet.addRow(rowData);
            dataRow.height = 20;

            dataRow.eachCell((cell, colNum) => {
                // Align first 5 columns left, rest center
                cell.alignment = { vertical: "middle", horizontal: colNum <= 5 ? "left" : "center" };

                // Apply background colors for attendance status columns (colNum > 5 and colNum <= totalDetailCols - 2)
                if (colNum > 5 && colNum <= totalDetailCols - 2) {
                    const val = cell.value;
                    if (val === "H") cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2CC" } };
                    else if (val === "WO") cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "E2EFDA" } };
                    else if (val === "A") cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FCE4D6" } };
                }

                // Optional: Add styling for Payable Days and LOP Days columns
                if (colNum === totalDetailCols - 1 || colNum === totalDetailCols) {
                    cell.font = { bold: true };
                    if (colNum === totalDetailCols && cell.value > 0) {
                        cell.font = { bold: true, color: { argb: "C00000" } }; // Red color for LOP days > 0
                    }
                }

                cell.border = {
                    top: { style: "thin", color: { argb: "D9D9D9" } },
                    left: { style: "thin", color: { argb: "D9D9D9" } },
                    bottom: { style: "thin", color: { argb: "D9D9D9" } },
                    right: { style: "thin", color: { argb: "D9D9D9" } }
                };
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/octet-stream" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `LoginTimeLog_${selectedDistrictName}.xlsx`;
        link.click();
    };

    // --- RENDER FUNCTIONS FOR SEPARATED VIEWS ---
    const renderSummaryTable = () => {
        return (
            <div className="final-table-project">
                {district ? (
                    <div className='final-mid-table-project'>
                        <table className="final-report-project">
                            <thead>
                                <tr>
                                    <th rowSpan={2}>S.No</th>
                                    <th rowSpan={2}>District Name</th>
                                    <th rowSpan={2}>Project Name</th>
                                    <th colSpan={7} className='noof-emp-color-project-header'>No of Employee</th>
                                    <th colSpan={7} className='total-days-inmon-project-color-header'>Total Days in Month</th>
                                    <th colSpan={7} className='noof-days-absent-project-color-header'>No of Days Absent</th>
                                    <th colSpan={7} className='noof-working-days-project-color-header'>No of Days Worked</th>
                                </tr>
                                <tr>
                                    <th style={{ backgroundColor: "#FFF2CC" }}>PM</th>
                                    <th style={{ backgroundColor: "#FFF2CC" }}>DC</th>
                                    <th style={{ backgroundColor: "#FFF2CC" }}>APM</th>
                                    <th style={{ backgroundColor: "#FFF2CC" }}>Help Desk</th>
                                    <th style={{ backgroundColor: "#FFF2CC" }}>Call Center</th>
                                    <th style={{ backgroundColor: "#FFF2CC" }}>CRO</th>
                                    <th style={{ backgroundColor: "#FFF2CC" }}>System Admin</th>
                                    <th style={{ backgroundColor: "#D9E1F2" }}>PM</th>
                                    <th style={{ backgroundColor: "#D9E1F2" }}>DC</th>
                                    <th style={{ backgroundColor: "#D9E1F2" }}>APM</th>
                                    <th style={{ backgroundColor: "#D9E1F2" }}>Help Desk</th>
                                    <th style={{ backgroundColor: "#D9E1F2" }}>Call Center</th>
                                    <th style={{ backgroundColor: "#D9E1F2" }}>CRO</th>
                                    <th style={{ backgroundColor: "#D9E1F2" }}>System Admin</th>
                                    <th style={{ backgroundColor: "#FCE4D6" }}>PM</th>
                                    <th style={{ backgroundColor: "#FCE4D6" }}>DC</th>
                                    <th style={{ backgroundColor: "#FCE4D6" }}>APM</th>
                                    <th style={{ backgroundColor: "#FCE4D6" }}>Help Desk</th>
                                    <th style={{ backgroundColor: "#FCE4D6" }}>Call Center</th>
                                    <th style={{ backgroundColor: "#FCE4D6" }}>CRO</th>
                                    <th style={{ backgroundColor: "#FCE4D6" }}>System Admin</th>
                                    <th style={{ backgroundColor: "#E2EFDA" }}>PM</th>
                                    <th style={{ backgroundColor: "#E2EFDA" }}>DC</th>
                                    <th style={{ backgroundColor: "#E2EFDA" }}>APM</th>
                                    <th style={{ backgroundColor: "#E2EFDA" }}>Help Desk</th>
                                    <th style={{ backgroundColor: "#E2EFDA" }}>Call Center</th>
                                    <th style={{ backgroundColor: "#E2EFDA" }}>CRO</th>
                                    <th style={{ backgroundColor: "#E2EFDA" }}>System Admin</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoadingSummary ? (
                                    <tr>
                                        <td colSpan="31" style={{ textAlign: "center", padding: "40px", fontWeight: "500", color: "#1F4E78" }}>
                                            <div className="table-loader-spinner">Loading Summary Records, please wait...</div>
                                        </td>
                                    </tr>
                                ) : attendanceData.length > 0 ? (
                                    <>
                                        <tr style={{ fontWeight: "bold", backgroundColor: "#FFE699" }}>
                                            <td colSpan={3}>Total</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.totalPmEmployees || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.totalPmdcEmployees || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.totalApmEmployees || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.totalHdoEmployees || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.totalCcoEmployees || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.totalCroEmployees || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.totalsystemAdminEmployees || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.pmTotal || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.pmdcTotal || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.apmTotal || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.hdoTotal || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.ccoTotal || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.croTotal || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.systemAdminTotal || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.pmAbsent || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.pmdcAbsent || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.apmAbsent || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.hdoAbsent || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.ccoAbsent || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.croAbsent || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.systemAdminAbsent || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.pmWorked || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.pmdcWorked || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.apmWorked || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.hdoWorked || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.ccoWorked || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.croWorked || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.systemAdminWorked || 0), 0))}</td>
                                        </tr>
                                        {attendanceData.map((row, index) => {
                                            const isFirstProjectRow = index % 2 === 0;
                                            return (
                                                <tr key={index}>
                                                    {isFirstProjectRow ? (
                                                        <>
                                                            <td rowSpan={2} style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                                                                {Math.floor(index / 2) + 1}
                                                            </td>
                                                            <td
                                                                rowSpan={2}
                                                                style={{ verticalAlign: 'middle', color: '#0056b3', textDecoration: 'underline', cursor: 'pointer', fontWeight: '500' }}
                                                                onClick={() => handleDistrictRowClick(row.districtId, row.districtName)}
                                                                title="Click to view detailed employee logins"
                                                            >
                                                                {row.districtName}
                                                            </td>
                                                        </>
                                                    ) : null}

                                                    <td>{row.projectName}</td>
                                                    <td>{row.totalPmEmployees || 0}</td>
                                                    <td>{row.totalPmdcEmployees || 0}</td>
                                                    <td>{row.totalApmEmployees || 0}</td>
                                                    <td>{row.totalHdoEmployees || 0}</td>
                                                    <td>{row.totalCcoEmployees || 0}</td>
                                                    <td>{row.totalCroEmployees || 0}</td>
                                                    <td>{row.totalsystemAdminEmployees || 0}</td>
                                                    <td>{row.pmTotal || 0}</td>
                                                    <td>{row.pmdcTotal || 0}</td>
                                                    <td>{row.apmTotal || 0}</td>
                                                    <td>{row.hdoTotal || 0}</td>
                                                    <td>{row.ccoTotal || 0}</td>
                                                    <td>{row.croTotal || 0}</td>
                                                    <td>{row.systemAdminTotal || 0}</td>
                                                    <td>{row.pmAbsent || 0}</td>
                                                    <td>{row.pmdcAbsent || 0}</td>
                                                    <td>{row.apmAbsent || 0}</td>
                                                    <td>{row.hdoAbsent || 0}</td>
                                                    <td>{row.ccoAbsent || 0}</td>
                                                    <td>{row.croAbsent || 0}</td>
                                                    <td>{row.systemAdminAbsent || 0}</td>
                                                    <td>{row.pmWorked || 0}</td>
                                                    <td>{row.pmdcWorked || 0}</td>
                                                    <td>{row.apmWorked || 0}</td>
                                                    <td>{row.hdoWorked || 0}</td>
                                                    <td>{row.ccoWorked || 0}</td>
                                                    <td>{row.croWorked || 0}</td>
                                                    <td>{row.systemAdminWorked || 0}</td>
                                                </tr>
                                            );
                                        })}
                                    </>
                                ) : (
                                    <tr>
                                        <td colSpan="31" style={{ textAlign: "center", padding: "25px", color: "#666" }}>
                                            No attendance data available. Fill parameters above and hit Generate.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className='final-mid-table-project'>
                        <table className="final-report-project">
                            <thead>
                                <tr>
                                    <th rowSpan={2}>S.No</th>
                                    <th rowSpan={2}>District Name</th>
                                    <th rowSpan={2}>Project Name</th>
                                    <th colSpan={5} className='noof-emp-color-project-header'>No of Employee</th>
                                    <th colSpan={5} className='total-days-inmon-project-color-header'>Total Days in Month</th>
                                    <th colSpan={5} className='noof-days-absent-project-color-header'>No of Days Absent</th>
                                    <th colSpan={5} className='noof-working-days-project-color-header'>No of Days Worked</th>
                                </tr>
                                <tr>
                                    <th style={{ backgroundColor: "#FFF2CC" }}>ITM</th>
                                    <th style={{ backgroundColor: "#FFF2CC" }}>CRO</th>
                                    <th style={{ backgroundColor: "#FFF2CC" }}>DEO</th>
                                    <th style={{ backgroundColor: "#FFF2CC" }}>HDO</th>
                                    <th style={{ backgroundColor: "#FFF2CC" }}>DBA</th>
                                    <th style={{ backgroundColor: "#D9E1F2" }}>ITM</th>
                                    <th style={{ backgroundColor: "#D9E1F2" }}>CRO</th>
                                    <th style={{ backgroundColor: "#D9E1F2" }}>DEO</th>
                                    <th style={{ backgroundColor: "#D9E1F2" }}>HDO</th>
                                    <th style={{ backgroundColor: "#D9E1F2" }}>DBA</th>
                                    <th style={{ backgroundColor: "#FCE4D6" }}>ITM</th>
                                    <th style={{ backgroundColor: "#FCE4D6" }}>CRO</th>
                                    <th style={{ backgroundColor: "#FCE4D6" }}>DEO</th>
                                    <th style={{ backgroundColor: "#FCE4D6" }}>HDO</th>
                                    <th style={{ backgroundColor: "#FCE4D6" }}>DBA</th>
                                    <th style={{ backgroundColor: "#E2EFDA" }}>ITM</th>
                                    <th style={{ backgroundColor: "#E2EFDA" }}>CRO</th>
                                    <th style={{ backgroundColor: "#E2EFDA" }}>DEO</th>
                                    <th style={{ backgroundColor: "#E2EFDA" }}>HDO</th>
                                    <th style={{ backgroundColor: "#E2EFDA" }}>DBA</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoadingSummary ? (
                                    <tr>
                                        <td colSpan="23" style={{ textAlign: "center", padding: "40px", fontWeight: "500", color: "#1F4E78" }}>
                                            <div className="table-loader-spinner">Loading Summary Records, please wait...</div>
                                        </td>
                                    </tr>
                                ) : attendanceData.length > 0 ? (
                                    <>
                                        <tr style={{ fontWeight: "bold", backgroundColor: "#FFE699" }}>
                                            <td colSpan={3}>Total</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.totalItmEmployees || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.totalCroEmployees || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.totalDeoEmployees || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.totalHdoEmployees || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.totaldbaEmployees || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.itmTotal || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.croTotal || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.deoTotal || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.hdoTotal || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.dbaTotal || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.itmAbsent || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.croAbsent || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.deoAbsent || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.hdoAbsent || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.dbaAbsent || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.itmWorked || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.croWorked || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.deoWorked || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.hdoWorked || 0), 0))}</td>
                                            <td>{formatNumber(attendanceData.reduce((sum, r) => sum + (r.dbaWorked || 0), 0))}</td>
                                        </tr>
                                        {attendanceData.map((row, index) => {
                                            const isFirstProjectRow = index % 2 === 0;
                                            return (
                                                <tr key={index}>
                                                    {isFirstProjectRow ? (
                                                        <>
                                                            <td rowSpan={2} style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                                                                {Math.floor(index / 2) + 1}
                                                            </td>
                                                            <td
                                                                rowSpan={2}
                                                                style={{ verticalAlign: 'middle', color: '#0056b3', textDecoration: 'underline', cursor: 'pointer', fontWeight: '500' }}
                                                                onClick={() => handleDistrictRowClick(row.districtId, row.districtName)}
                                                                title="Click to view detailed employee logins"
                                                            >
                                                                {row.districtName}
                                                            </td>
                                                        </>
                                                    ) : null}

                                                    <td>{row.projectName}</td>
                                                    <td>{row.totalItmEmployees || 0}</td>
                                                    <td>{row.totalCroEmployees || 0}</td>
                                                    <td>{row.totalDeoEmployees || 0}</td>
                                                    <td>{row.totalHdoEmployees || 0}</td>
                                                    <td>{row.totaldbaEmployees || 0}</td>
                                                    <td>{row.itmTotal || 0}</td>
                                                    <td>{row.croTotal || 0}</td>
                                                    <td>{row.deoTotal || 0}</td>
                                                    <td>{row.hdoTotal || 0}</td>
                                                    <td>{row.dbaTotal || 0}</td>
                                                    <td>{row.itmAbsent || 0}</td>
                                                    <td>{row.croAbsent || 0}</td>
                                                    <td>{row.deoAbsent || 0}</td>
                                                    <td>{row.hdoAbsent || 0}</td>
                                                    <td>{row.dbaAbsent || 0}</td>
                                                    <td>{row.itmWorked || 0}</td>
                                                    <td>{row.croWorked || 0}</td>
                                                    <td>{row.deoWorked || 0}</td>
                                                    <td>{row.hdoWorked || 0}</td>
                                                    <td>{row.dbaWorked || 0}</td>
                                                </tr>
                                            );
                                        })}
                                    </>
                                ) : (
                                    <tr>
                                        <td colSpan="23" style={{ textAlign: "center", padding: "25px", color: "#666" }}>
                                            No attendance data available. Fill parameters above and hit Generate.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    const renderDetailTable = () => {
        return (
            <div className="login-time-div" style={{ marginTop: '25px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <button
                        type="button"
                        className="fina-report-project-btn"
                        onClick={() => setViewLevel("SUMMARY")}
                        style={{ backgroundColor: '#6c757d' }}
                    >
                        ← Back to Summary Table
                    </button>
                    <h3 style={{ margin: 0, fontSize: '18px', color: '#2E75B6' }}>
                        Daily Login Logs Workspace: {selectedDistrictName}
                    </h3>
                    <button type="button" className="fina-report-project-btn" onClick={generateExcelDetail} disabled={isLoadingDetail || dayWiseLoginData.length === 0}>
                        Download Day Log Excel
                    </button>
                </div>

                <div className="login-time-wrapper" style={{ overflowX: 'auto', background: '#fff', padding: '10px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}>
                    <table className="login-time-table final-report-project">
                        <thead>
                            <tr style={{ backgroundColor: '#5B9BD5', color: '#fff' }}>
                                <th>S.No</th>
                                <th>District</th>
                                <th>Employee Name</th>
                                <th>Designation</th>
                                <th>Contact Number</th>
                                {Array.from({ length: totalDaysInSelectedMonth }, (_, i) => (
                                    <th key={i + 1} className="text-nowrap" style={{ padding: '8px 12px' }}>
                                        Day-{i + 1}
                                    </th>
                                ))}
                                <th>Payable Days</th>
                                <th>LOP Days</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoadingDetail ? (
                                <tr>
                                    <td colSpan={totalDaysInSelectedMonth + 5} style={{ textAlign: "center", padding: "40px", fontWeight: "500", color: "#2E75B6" }}>
                                        <div className="table-loader-spinner">Fetching day-wise employee login timestamps, please hold...</div>
                                    </td>
                                </tr>
                            ) : dayWiseLoginData.length > 0 ? (
                                dayWiseLoginData.map((employee, index) => (
                                    <tr key={index}>
                                        <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                        <td>{employee.districtName}</td>
                                        <td style={{ fontWeight: '500' }}>{employee.fullName}</td>
                                        <td>{employee.designation}</td>
                                        <td>{employee.contactNumber}</td>
                                        {Array.from({ length: totalDaysInSelectedMonth }, (_, dayIndex) => {
                                            const day = dayIndex + 1;
                                            const value = employee.dayWiseStatus[day] || "-";

                                            let cellStyle = {};
                                            if (value === "H") cellStyle = { backgroundColor: "#FFF2CC", textAlign: 'center', fontWeight: 'bold' };
                                            else if (value === "WO") cellStyle = { backgroundColor: "#E2EFDA", textAlign: 'center', fontWeight: 'bold' };
                                            else if (value === "A") cellStyle = { backgroundColor: "#FCE4D6", textAlign: 'center', fontWeight: 'bold', color: '#c00000' };
                                            else if (value !== "-") cellStyle = { color: '#1f4e78', fontWeight: '500', textAlign: 'center' };
                                            else cellStyle = { textAlign: 'center', color: '#ccc' };

                                            return (
                                                <td key={dayIndex} style={cellStyle}>
                                                    {timeConverter(value)}
                                                </td>
                                            );
                                        })}
                                        <td>{employee.payableDays}</td>
                                        <td>{employee.lopDays}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={totalDaysInSelectedMonth + 5} style={{ textAlign: 'center', padding: '20px' }}>
                                        No logs mapped for this district.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className="fina-report-project-container">
            <div className="fina-report-project-box">
                <h2 className="fina-report-project-heading">Project Wise Final Attendance Report</h2>
                {errors.general && <div className="error-message">{errors.general}</div>}
                <form onSubmit={handleSubmit}>
                    <div className="fina-report-project-maincontainer">
                        <div className="fina-report-project-container-part">
                            <div className={`fina-report-project-floating-label-container ${isFocused.state || state ? 'float-label' : ''}`}>
                                <Select
                                    name="state"
                                    options={stateOptions}
                                    value={stateOptions.find(opt => opt.value === state) || null}
                                    onChange={option => handleInputChange(option, 'state')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, state: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, state: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label><span className="required">*</span>State</label>
                                {errors.state && <div className="error-message">{errors.state}</div>}
                            </div>
                        </div>
                        <div className="fina-report-project-container-part">
                            <div className={`fina-report-project-floating-label-container ${isFocused.district || district !== null ? 'float-label' : ''}`}>
                                <Select
                                    name="district"
                                    options={districtOptions}
                                    value={districtOptions.find(opt => opt.value === district) || null}
                                    onChange={option => handleInputChange(option, 'district')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, district: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, district: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label>Working District</label>
                            </div>
                        </div>
                    </div>
                    <div className="fina-report-project-maincontainer">
                        <div className="fina-report-project-container-part">
                            <div className={`fina-report-project-floating-label-container ${isFocused.year || year ? 'float-label' : ''}`}>
                                <DatePicker
                                    selected={year ? new Date(year, 0, 1) : null}
                                    onChange={handleYearChange}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, year: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, year: false }))}
                                    dateFormat="yyyy"
                                    showYearPicker
                                    placeholderText=""
                                    className="fina-report-project-date-picker"
                                    minDate={new Date(2000, 0, 1)}
                                    maxDate={new Date(currentYear, 11, 31)}
                                    ref={yearInputRef}
                                />
                                <label><span className="required">*</span>Year</label>
                                {errors.year && <div className="error-message">{errors.year}</div>}
                            </div>
                        </div>
                        <div className="fina-report-project-container-part">
                            <div className={`fina-report-project-floating-label-container ${isFocused.month || month ? 'float-label' : ''}`}>
                                <Select
                                    name="month"
                                    options={monthOptions}
                                    value={monthOptions.find(opt => opt.value === month) || null}
                                    onChange={option => handleInputChange(option, 'month')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, month: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, month: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label><span className="required">*</span>Month</label>
                                {errors.month && <div className="error-message">{errors.month}</div>}
                            </div>
                        </div>
                    </div>
                    <div className="fina-report-project-maincontainer">
                        <div className="fina-report-project-container-part">
                            <div className={`fina-report-project-floating-label-container ${isFocused.clients || clients ? 'float-label' : ''}`}>
                                <Select
                                    name="clients"
                                    options={clientResponse}
                                    value={clientResponse.find(opt => opt.value === clients) || null}
                                    onChange={option => handleInputChange(option, 'clients')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, clients: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, clients: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label><span className="required">*</span>Client</label>
                            </div>
                        </div>
                        <div className="fina-report-project-container-part">
                            <div className="fina-report-project-btn-div">
                                <button type="submit" className="fina-report-project-btn" disabled={!isFetchDataEnabled || isLoadingSummary}>
                                    {isLoadingSummary ? "Processing..." : "Generate Report"}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>

            {viewLevel === "SUMMARY" && (
                <div className="fina-report-project-btn-div" style={{ marginTop: '15px' }}>
                    <button type="button" className="fina-report-project-btn" onClick={generateExcelSummary} disabled={attendanceData.length === 0 || isLoadingSummary}>
                        Download Summary Excel
                    </button>
                </div>
            )}

            {/* DYNAMIC RENDERING INTERFACES */}
            {viewLevel === "SUMMARY" ? renderSummaryTable() : renderDetailTable()}
        </div>
    );
};

export default ProjectWiseFinalAttendanceReport;